import Instrument from './Instrument.js';
import Tone from 'tone';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {METAL_SYNTH} from 'constants/instrument-types.js';

class MetalSynth extends Instrument {
    constructor(sequencerEngine, mixSession, context) {
        super();
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.mixSession = mixSession;
        this.context = context;

        this.synth = new Tone.MetalSynth();

        this.setupParameters();
    }

    getInstrumentType() {
        return METAL_SYNTH;
    }

    getName() {
        return 'Metal Synth';
    }

    setupParameters() {
        this.frequencyParameter = new AbstractParameter(
            'Frequency', this,
            (o, x) => this.synth.frequency.value = x,
            {
                min: 0,
                max: 15000,
                unit: '',
                defaultValue: 200,
            });

        this.registerParameter(this.frequencyParameter);

        this.resonanceParameter = new AbstractParameter(
            'Resonance', this,
            (o, x) => this.synth.resonance = x,
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 100,
            });

        this.registerParameter(this.resonanceParameter);

        this.harmonicityParameter = new AbstractParameter(
            'Harmonicity', this,
            (o, x) => this.synth.harmonicity = x,
            {
                min: 0,
                max: 200,
                unit: '',
                defaultValue: 100,
            });

        this.registerParameter(this.harmonicityParameter);

        this.modulationIndexParameter = new AbstractParameter(
            'Modulation Index', this,
            (o, x) => this.synth.modulationIndex = x,
            {
                min: 0,
                max: 200,
                unit: '',
                defaultValue: 20,
            });

        this.registerParameter(this.modulationIndexParameter);

        this.attackParameter = new AbstractParameter(
            'Attack', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 10
            });
        this.registerParameter(this.attackParameter);
        this.decayParameter = new AbstractParameter(
            'Decay', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 200
            });
        this.registerParameter(this.decayParameter);
        this.sustainParameter = new AbstractParameter(
            'Sustain', this, (o, x) => x, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 1
            });
        this.registerParameter(this.sustainParameter);
        this.releaseParameter = new AbstractParameter(
            'Release', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 1000
            });
        this.registerParameter(this.releaseParameter);
    }

    get frequency() {
        return this.frequencyParameter.get();
    }

    set frequency(x) {
        this.frequencyParameter.set(x);
    }

    get resonance() {
        return this.resonanceParameter.get();
    }

    set resonance(x) {
        this.resonanceParameter.set(x);
    }

    get harmonicity() {
        return this.harmonicityParameter.get();
    }

    set harmonicity(x) {
        this.harmonicityParameter.set(x);
    }

    get modulationIndex() {
        return this.modulationIndexParameter.get();
    }

    set modulationIndex(x) {
        this.modulationIndexParameter.set(x);
    }

    get ampAttack() {
        return this.attackParameter.get();
    }

    set ampAttack(s) {
        this.attackParameter.set(s);
    }

    get ampDecay() {
        return this.decayParameter.get();
    }

    set ampDecay(decay) {
        this.decayParameter.set(decay);
    }
    
    get ampSustain() {
        return this.sustainParameter.get();
    }

    set ampSustain(sustain) {
        this.sustainParameter.set(sustain);
    }

    get ampRelease() {
        return this.releaseParameter.get();
    }

    set ampRelease(release) {
        this.releaseParameter.set(release);
    }

    setEnvelopes(stepData) {
        let envelopes = this.getEnvelopes();

        // set the attack and release of this trigger
        envelopes.forEach(
            envelope =>
                envelope.attack = new Tone.Time((1 + this.ampAttack) / 1000.0));
        envelopes.forEach(
            envelope =>
                envelope.release = new Tone.Time((1 + this.ampRelease) / 1000.0));
        envelopes.forEach(
            envelope =>
                envelope.sustain = this.ampSustain);
        envelopes.forEach(
            envelope =>
                envelope.decay = new Tone.Time((1 + this.ampDecay) / 1000.0));
    }

    getInstrument() {
        return this.synth;
    }

    getEnvelopes() {
        return [this.synth.envelope];
    }

    hasNotes() {
        return false;
    }

    getJson() {
        let json = {
            ... super.getJson(),
            frequency: this.frequency,
            resonance: this.resonance,
            harmonicity: this.harmonicity,
            modulationIndex: this.modulationIndex,
            ampAttack: this.ampAttack,
            ampDecay: this.ampDecay,
            ampSustain: this.ampSustain,
            ampRelease: this.ampRelease
        };

        json = saveParameterToJson(
            json, 'frequencyParameter', this.frequencyParameter);
        json = saveParameterToJson(
            json, 'resonanceParameter', this.resonanceParameter);
        json = saveParameterToJson(
            json, 'harmonicityParameter', this.harmonicityParameter);
        json = saveParameterToJson(
            json, 'modulationIndexParameter', this.modulationIndexParameter);
        json = saveParameterToJson(
            json, 'attackParameter', this.attackParameter);
        json = saveParameterToJson(
            json, 'decayParameter', this.decayParameter);
        json = saveParameterToJson(
            json, 'sustainParameter', this.sustainParameter);
        json = saveParameterToJson(
            json, 'releaseParameter', this.releaseParameter);

        return json;
    }

    fromJson(json) {
        this.frequency = json.frequency;
        this.resonance = json.resonance;
        this.harmonicity = json.harmonicity;
        this.modulationIndex = json.modulationIndex;
        this.ampAttack = json.ampAttack;
        this.ampDecay = json.ampDecay;
        this.ampSustain = json.ampSustain;
        this.ampRelease = json.ampRelease;

        loadParameterFromJson(
            json, 'frequencyParameter', this.frequencyParameter);
        loadParameterFromJson(
            json, 'resonanceParameter', this.resonanceParameter);
        loadParameterFromJson(
            json, 'modulationIndexParameter', this.modulationIndexParameter);
        loadParameterFromJson(
            json, 'harmonicityParameter', this.harmonicityParameter);
        loadParameterFromJson(
            json, 'attackParameter', this.attackParameter);
        loadParameterFromJson(
            json, 'decayParameter', this.decayParameter);
        loadParameterFromJson(
            json, 'sustainParameter', this.sustainParameter);
        loadParameterFromJson(
            json, 'releaseParameter', this.releaseParameter);
    }
}

export default MetalSynth;
